/**
 * Documents & photos.
 *
 * Upload flow: `POST /documents/uploads` returns a pre-signed PUT URL; the
 * browser uploads straight to object storage, then `POST .../finalize`
 * marks the version ready. Progress is reported per file.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useRef, useState } from "react";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import {
  compactParams,
  type ListState,
  type PageOf,
  type Paged,
  usePagedQuery,
  usePageQuery,
} from "@/shared/lib/paging";

export type Document = Schemas["DocumentOut"];
export type DocumentVersion = Schemas["VersionOut"];

/** Record a reasoned retention decision while preserving legal hold. */
export function useReviewRetention(): UseMutationResult<
  Document,
  Error,
  { id: string; body: Schemas["RetentionReviewIn"] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.POST("/api/v1/documents/{document_id}/retention-review", {
          params: { path: { document_id: id } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
    },
  });
}

/** Remove a mutable record link; frozen-record conflicts remain actionable errors. */
export function useUnlinkDocument(): UseMutationResult<
  void,
  Error,
  { id: string; linkId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, linkId }) => {
      unwrap(
        await api.DELETE("/api/v1/documents/{document_id}/links/{link_id}", {
          params: { path: { document_id: id, link_id: linkId } },
        }),
      );
    },
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history", input.id] });
    },
  });
}
export type UploadInit = Schemas["UploadInitIn"];
export type DocumentPatch = Schemas["DocumentPatch"];
export type DocumentCategory = Schemas["DocumentCategoryOut"];
export type RetentionPolicy = Schemas["RetentionPolicyOut"];
export type DocumentHistory = Schemas["HistoryOut"];
export interface RetentionPolicyInput {
  name: string;
  retain_years: number;
  action_on_expiry: "archive" | "delete" | "review";
  legal_hold_allowed: boolean;
}

export const DOCUMENT_KINDS = [
  "document",
  "photo",
  "drawing",
  "manual",
  "certificate",
  "report",
] as const;

/** Entity types documents can be attached to (mirrors the backend entity registry). */
export type LinkableEntity =
  | "asset"
  | "workorder"
  | "request"
  | "jobtemplate"
  | "item"
  | "party"
  | "site"
  | "location"
  | "plan"
  | "inventoryoperation";

/** Search and filter options for the document library. */
export interface DocumentFilters {
  q?: string;
  kind?: string;
  category_id?: string;
  status?: string;
  linked?: boolean;
  tags?: string;
  entity_type?: string;
  entity_id?: string;
}

/** Search documents with cursor pagination. */
export function useDocuments(filters: DocumentFilters, enabled = true): Paged<Document> {
  return usePagedQuery(
    ["documents", "library", filters],
    async (cursor) =>
      unwrap(
        await api.GET("/api/v1/documents/", {
          params: {
            query: compactParams({ ...filters, cursor, limit: 50 }),
          },
        }),
      ),
    { enabled },
  );
}

/** Fetch a server-sorted numbered document library page without changing cursor consumers. */
export function useDocumentPage(
  filters: DocumentFilters,
  state: ListState,
  enabled = true,
): PageOf<Document> {
  return usePageQuery(
    ["documents", "library-page", filters],
    state,
    async (params) =>
      unwrap(
        await api.GET("/api/v1/documents/", {
          params: { query: compactParams({ ...filters, ...params }) },
        }),
      ),
    { enabled },
  );
}

/** Fetch independently reviewable revisions as a numbered server-sorted table. */
export function usePublicationReviewPage(
  state: ListState,
  enabled = true,
): PageOf<Schemas["PublicationReviewOut"]> {
  return usePageQuery(
    ["documents", "publication-review-page"],
    state,
    async (params) =>
      unwrap(
        await api.GET("/api/v1/documents/publication-review-queue", {
          params: { query: compactParams({ ...params }) },
        }),
      ),
    { enabled },
  );
}

/** Fetch retention reviews as a numbered server-sorted table. */
export function useRetentionReviewPage(
  state: ListState,
  enabled = true,
): PageOf<Schemas["ReviewQueueOut"]> {
  return usePageQuery(
    ["documents", "retention-review-page"],
    state,
    async (params) =>
      unwrap(
        await api.GET("/api/v1/documents/review-queue", {
          params: { query: compactParams({ ...params }) },
        }),
      ),
    { enabled },
  );
}

/** Pending revisions eligible for independent publication review, separate from retention. */
export function usePublicationReviews(enabled: boolean): Paged<Schemas["PublicationReviewOut"]> {
  return usePagedQuery(
    ["documents", "publication-reviews"],
    async (cursor) =>
      unwrap(
        await api.GET("/api/v1/documents/publication-review-queue", {
          params: { query: compactParams({ cursor, limit: 50 }) },
        }),
      ),
    { enabled },
  );
}

/** Tenant document categories. */
export function useDocumentCategories(): UseQueryResult<DocumentCategory[]> {
  return useQuery({
    queryKey: ["documents", "categories"],
    queryFn: async () => unwrap(await api.GET("/api/v1/documents/categories")),
  });
}

/** Load one document's detail through its permission- and site-scoped endpoint. */
export function useDocument(documentId: string): UseQueryResult<Document> {
  return useQuery({
    queryKey: ["documents", documentId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/documents/{document_id}", {
          params: { path: { document_id: documentId } },
        }),
      ),
    enabled: !!documentId,
  });
}

/** Read immutable lifecycle events for a document. */
export function useDocumentHistory(documentId: string): UseQueryResult<DocumentHistory[]> {
  return useQuery({
    queryKey: ["document-history", documentId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/documents/{document_id}/history", {
          params: { path: { document_id: documentId } },
        }),
      ),
    enabled: !!documentId,
  });
}

/** Tenant retention policies; only users with category administration permission may query. */
export function useRetentionPolicies(enabled = true): UseQueryResult<RetentionPolicy[]> {
  return useQuery({
    queryKey: ["documents", "retention-policies"],
    queryFn: async () => unwrap(await api.GET("/api/v1/documents/retention-policies")),
    enabled,
  });
}

/** Create a category or retention policy in the tenant's document settings. */
export function useCreateDocumentSettings(): UseMutationResult<
  DocumentCategory | RetentionPolicy,
  Error,
  | { type: "category"; body: Schemas["DocumentCategoryIn"] }
  | { type: "retention"; body: RetentionPolicyInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      if (input.type === "category") {
        return unwrap(await api.POST("/api/v1/documents/categories", { body: input.body }));
      }
      return unwrap(await api.POST("/api/v1/documents/retention-policies", { body: input.body }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents", "categories"] });
      void qc.invalidateQueries({ queryKey: ["documents", "retention-policies"] });
    },
  });
}

/** Update category approval settings or a tenant retention rule. */
export function useUpdateDocumentSettings(): UseMutationResult<
  DocumentCategory | RetentionPolicy,
  Error,
  | { type: "category"; id: string; body: Schemas["DocumentCategoryIn"] }
  | { type: "retention"; id: string; body: RetentionPolicyInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      if (input.type === "category") {
        return unwrap(
          await api.PATCH("/api/v1/documents/categories/{category_id}", {
            params: { path: { category_id: input.id } },
            body: input.body,
          }),
        );
      }
      return unwrap(
        await api.PATCH("/api/v1/documents/retention-policies/{policy_id}", {
          params: { path: { policy_id: input.id } },
          body: input.body,
        }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents", "categories"] });
      void qc.invalidateQueries({ queryKey: ["documents", "retention-policies"] });
    },
  });
}

/** Retention review queue containing approaching and overdue review dates. */
export function useRetentionReviewQueue(enabled = true): Paged<Schemas["ReviewQueueOut"]> {
  return usePagedQuery(
    ["documents", "retention-review"],
    async (cursor) =>
      unwrap(
        await api.GET("/api/v1/documents/review-queue", {
          params: { query: compactParams({ cursor, limit: 50 }) },
        }),
      ),
    { enabled },
  );
}

/** Documents linked to an entity. */
export function useEntityDocuments(
  entityType: LinkableEntity,
  entityId: string,
  enabled = true,
): UseQueryResult<Document[]> {
  return useQuery({
    queryKey: ["documents", entityType, entityId],
    queryFn: async () => {
      const items: Document[] = [];
      let cursor: string | undefined;
      do {
        const page = unwrap(
          await api.GET("/api/v1/documents/", {
            params: {
              query: compactParams({
                entity_type: entityType,
                entity_id: entityId,
                limit: 100,
                cursor,
              }),
            },
          }),
        );
        items.push(...page.items);
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return items;
    },
    enabled,
  });
}

/** Versions of a document. */
export function useDocumentVersions(documentId: string | null): UseQueryResult<DocumentVersion[]> {
  return useQuery({
    queryKey: ["document-versions", documentId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/documents/{document_id}/versions", {
          params: { path: { document_id: documentId ?? "" } },
        }),
      ),
    enabled: !!documentId,
  });
}

/** Resolve a short-lived download URL and open it in a new tab. */
export async function openDocument(documentId: string, versionId?: string): Promise<void> {
  const out = unwrap(
    await api.GET("/api/v1/documents/{document_id}/download", {
      params: {
        path: { document_id: documentId },
        query: { version_id: versionId },
      },
    }),
  );
  globalThis.open(out.url, "_blank", "noopener");
}

/** Open the exact unpublished bytes only through the author/reviewer-scoped endpoint. */
export async function openDocumentRevision(documentId: string, versionId: string): Promise<void> {
  const out = unwrap(
    await api.GET("/api/v1/documents/{document_id}/versions/{version_id}/download", {
      params: { path: { document_id: documentId, version_id: versionId } },
    }),
  );
  globalThis.open(out.url, "_blank", "noopener");
}

/** Edit document metadata. */
export function useUpdateDocument(): UseMutationResult<
  Document,
  Error,
  { id: string; body: DocumentPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.PATCH("/api/v1/documents/{document_id}", {
          params: { path: { document_id: id } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
    },
  });
}

/** Archive a document. */
export function useArchiveDocument(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.POST("/api/v1/documents/{document_id}/archive", {
          params: { path: { document_id: id } },
          body: { reason: "Archived from the attachment panel" },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
    },
  });
}

/** Link an existing document without uploading duplicate file bytes. */
export function useLinkDocument(): UseMutationResult<
  Schemas["LinkOut"],
  Error,
  { id: string; entity_type: LinkableEntity; entity_id: string; relation?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) =>
      unwrap(
        await api.POST("/api/v1/documents/{document_id}/links", {
          params: { path: { document_id: id } },
          body: { ...body, relation: body.relation ?? "attachment", version_id: null },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
    },
  });
}

/** Submit a controlled revision for independent review. */
export function useSubmitDocumentVersion(): UseMutationResult<
  DocumentVersion,
  Error,
  { id: string; versionId: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, versionId, reason }) =>
      unwrap(
        await api.POST("/api/v1/documents/{document_id}/versions/{version_id}/submit", {
          params: { path: { document_id: id, version_id: versionId } },
          body: { reason },
        }),
      ),
    onSuccess: (_out, input) => {
      void qc.invalidateQueries({ queryKey: ["documents", input.id] });
      void qc.invalidateQueries({ queryKey: ["document-versions", input.id] });
      void qc.invalidateQueries({ queryKey: ["document-history", input.id] });
      void qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** Approve or reject a submitted document revision. */
export function useReviewDocumentVersion(
  decision: "approve" | "reject",
): UseMutationResult<
  Document | DocumentVersion,
  Error,
  { id: string; versionId: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, versionId, reason }) => {
      if (decision === "approve") {
        return unwrap(
          await api.POST("/api/v1/documents/{document_id}/versions/{version_id}/approve", {
            params: { path: { document_id: id, version_id: versionId } },
            body: { reason },
          }),
        );
      }
      return unwrap(
        await api.POST("/api/v1/documents/{document_id}/versions/{version_id}/reject", {
          params: { path: { document_id: id, version_id: versionId } },
          body: { reason },
        }),
      );
    },
    onSuccess: (_out, input) => {
      void qc.invalidateQueries({ queryKey: ["documents", input.id] });
      void qc.invalidateQueries({ queryKey: ["document-versions", input.id] });
      void qc.invalidateQueries({ queryKey: ["document-history", input.id] });
      void qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** Archive a document with a required reason. */
export function useArchiveDocumentWithReason(): UseMutationResult<
  Document,
  Error,
  { id: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) =>
      unwrap(
        await api.POST("/api/v1/documents/{document_id}/archive", {
          params: { path: { document_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
    },
  });
}

/** Restore an archived document with a required reason. */
export function useRestoreDocument(): UseMutationResult<
  Document,
  Error,
  { id: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) =>
      unwrap(
        await api.POST("/api/v1/documents/{document_id}/restore", {
          params: { path: { document_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
    },
  });
}

/** Change legal hold under the separate governance permission. */
export function useSetDocumentHold(): UseMutationResult<
  Document,
  Error,
  { id: string; legal_hold: boolean; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) =>
      unwrap(
        await api.POST("/api/v1/documents/{document_id}/legal-hold", {
          params: { path: { document_id: id } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
    },
  });
}

/** Per-file upload state. */
export interface UploadState {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "failed" | "ready";
  error?: string;
  documentId?: string;
  versionId?: string;
}

/** Mobile photo pickers may omit the MIME type for HEIC/HEIF and other images. */
export function fileMimeType(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const images: Record<string, string> = {
    heic: "image/heic",
    heif: "image/heif",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
  };
  return images[extension] ?? "application/octet-stream";
}

/** Compute the SHA-256 of a file (hex), when SubtleCrypto is available. */
export async function sha256Hex(file: File): Promise<string | null> {
  const subtle = globalThis.crypto.subtle as SubtleCrypto | undefined;
  if (!subtle) {
    return null;
  }
  const digest = await subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** PUT a file to a pre-signed URL with progress callbacks. */
export function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e): void => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = (): void => reject(new Error("Upload failed"));
    xhr.onabort = (): void => reject(new DOMException("Upload cancelled", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    if (signal?.aborted) {
      reject(new DOMException("Upload cancelled", "AbortError"));
      return;
    }
    xhr.send(file);
  });
}

/** Options for `useUploader`. */
export interface UploaderOptions {
  kind?: (typeof DOCUMENT_KINDS)[number];
  title?: string;
  categoryId?: string;
  categoryKey?: string;
  changeNote?: string;
  isConfidential?: boolean;
  links?: { entity_type: LinkableEntity; entity_id: string; relation?: string }[];
  /** Replace an existing document with a new version. */
  documentId?: string;
  staged?: boolean;
  onUploaded?: (documentId: string, file: File) => void;
}

/** Result of `useUploader`. */
export interface Uploader {
  uploads: UploadState[];
  upload: (files: FileList | File[]) => Promise<void>;
  clear: () => void;
  retry: (id: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
  busy: boolean;
}

/** Upload one or more files through the pre-signed flow. */
export function useUploader(options: UploaderOptions): Uploader {
  const qc = useQueryClient();
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const rows = useRef<UploadState[]>([]);
  const controllers = useRef(new Map<string, AbortController>());
  const tasks = useRef(new Map<string, Promise<void>>());
  const cancelled = useRef(new Set<string>());
  const patch = (id: string, p: Partial<UploadState>): void => {
    rows.current = rows.current.map((u) => (u.id === id ? { ...u, ...p } : u));
    setUploads([...rows.current]);
  };

  const run = async (row: UploadState): Promise<void> => {
    const { id, file } = row;
    const controller = new AbortController();
    controllers.current.set(id, controller);
    try {
      patch(id, { status: "uploading", error: undefined, progress: 0 });
      const mimeType = fileMimeType(file);
      const kind = options.kind ?? (mimeType.startsWith("image/") ? "photo" : "document");
      const payload: Schemas["UploadInitIn"] = {
        staged: options.staged ?? false,
        upload_id: id,
        document_id: options.documentId ?? null,
        title: options.title ?? file.name.replace(/\.[^.]+$/, ""),
        kind,
        category_id: options.categoryId ?? null,
        category_key: options.categoryKey ?? null,
        filename: file.name,
        mime_type: mimeType,
        size_bytes: file.size,
        change_note: options.changeNote ?? "",
        is_confidential: options.isConfidential ?? false,
        links: (options.links ?? []).map((link) => ({
          ...link,
          relation: link.relation ?? "attachment",
        })),
      };
      const init = row.versionId
        ? unwrap(
            await api.POST("/api/v1/documents/versions/{version_id}/retry", {
              params: { path: { version_id: row.versionId } },
            }),
          )
        : unwrap(
            await api.POST("/api/v1/documents/uploads", {
              body: payload,
            }),
          );
      patch(id, { documentId: init.document_id, versionId: init.version_id });
      if (cancelled.current.has(id)) {
        return;
      }
      if (!init.ready) {
        await putWithProgress(
          init.upload_url,
          file,
          init.headers,
          (pct) => patch(id, { progress: pct }),
          controller.signal,
        );
        if (cancelled.current.has(id)) return;
        patch(id, { progress: 100 });
        const sha256 = await sha256Hex(file);
        unwrap(
          await api.POST("/api/v1/documents/versions/{version_id}/finalize", {
            params: { path: { version_id: init.version_id } },
            body: { sha256 },
          }),
        );
      }
      if (cancelled.current.has(id)) return;
      patch(id, { status: "ready", documentId: init.document_id });
      options.onUploaded?.(init.document_id, file);
    } catch (err) {
      if (!cancelled.current.has(id)) {
        patch(id, { status: "failed", error: err instanceof Error ? err.message : String(err) });
        const versionId = rows.current.find((u) => u.id === id)?.versionId;
        if (versionId) {
          await api
            .POST("/api/v1/documents/versions/{version_id}/fail", {
              params: { path: { version_id: versionId } },
            })
            .catch(() => undefined);
        }
      }
    } finally {
      controllers.current.delete(id);
      void qc.invalidateQueries({ queryKey: ["documents"] });
      void qc.invalidateQueries({ queryKey: ["document-history"] });
      void qc.invalidateQueries({ queryKey: ["document-versions"] });
    }
  };

  const upload = async (files: FileList | File[]): Promise<void> => {
    const list = [...files].map((file): UploadState => ({
      id: crypto.randomUUID(),
      file,
      progress: 0,
      status: "pending",
    }));
    rows.current = [...rows.current, ...list];
    setUploads([...rows.current]);
    await Promise.all(
      list.map((row) => {
        const task = run(row);
        tasks.current.set(row.id, task);
        return task;
      }),
    );
  };

  const cancel = async (id: string): Promise<void> => {
    if (!rows.current.some((u) => u.id === id)) return;
    cancelled.current.add(id);
    controllers.current.get(id)?.abort();
    await tasks.current.get(id);
    const row = rows.current.find((u) => u.id === id);
    if (!row) return;
    if (row.versionId) {
      try {
        unwrap(
          await api.POST("/api/v1/documents/versions/{version_id}/cancel", {
            params: { path: { version_id: row.versionId } },
          }),
        );
      } catch (error) {
        cancelled.current.delete(id);
        patch(id, { status: "failed", error: String(error) });
        throw error;
      }
    } else {
      try {
        unwrap(
          await api.POST("/api/v1/documents/uploads/{upload_id}/cancel", {
            params: { path: { upload_id: id } },
          }),
        );
      } catch (error) {
        cancelled.current.delete(id);
        patch(id, { status: "failed", error: String(error) });
        throw error;
      }
    }
    rows.current = rows.current.filter((u) => u.id !== id);
    setUploads([...rows.current]);
    void qc.invalidateQueries({ queryKey: ["documents"] });
    void qc.invalidateQueries({ queryKey: ["document-history"] });
    void qc.invalidateQueries({ queryKey: ["document-versions"] });
  };

  return {
    uploads,
    upload,
    clear: () => {
      rows.current = rows.current.filter((row) => row.status !== "ready" || options.staged);
      setUploads([...rows.current]);
    },
    retry: async (id) => {
      await tasks.current.get(id);
      const row = rows.current.find((u) => u.id === id);
      if (row?.status === "failed" && !controllers.current.has(id)) {
        const task = run(row);
        tasks.current.set(id, task);
        await task;
      }
    },
    cancel,
    busy: uploads.some((u) => u.status === "uploading" || u.status === "pending"),
  };
}
